import React, { useState, useEffect, useRef } from 'react';
import { geminiLiveService, LiveSessionStatus, LiveTranscriptItem } from '../services/geminiLiveService';
import { triggerHaptic } from '../services/purrSynthesizer';
import { Mic, MicOff, X, Radio, Sparkles, Volume2, ShieldCheck, Activity, Send } from 'lucide-react';

interface GeminiLiveVoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GeminiLiveVoiceModal: React.FC<GeminiLiveVoiceModalProps> = ({ isOpen, onClose }) => {
  const [status, setStatus] = useState<LiveSessionStatus>('disconnected');
  const [transcript, setTranscript] = useState<LiveTranscriptItem[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      if (status !== 'disconnected') {
        geminiLiveService.stopLiveSession();
      }
      return;
    }

    const unsubStatus = geminiLiveService.onStatusChange((s) => setStatus(s));
    const unsubTranscript = geminiLiveService.onTranscriptChange((t) => setTranscript([...t]));

    // Start session when modal opens
    geminiLiveService.startLiveSession();
    setIsMuted(geminiLiveService.getIsMuted());

    return () => {
      unsubStatus();
      unsubTranscript();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isOpen]);

  // Audio-reactive visualizer loop
  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let angle = 0;
    const render = () => {
      const data = geminiLiveService.getFrequencyData();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const baseRadius = 55;
      const numBars = 36;

      angle += 0.015;

      // Draw outer glowing resonance ring
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius - 5, 0, Math.PI * 2);
      ctx.strokeStyle = status === 'speaking' ? 'rgba(234, 179, 8, 0.4)' : 'rgba(56, 189, 248, 0.3)';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw audio frequency spokes (Clockwork Spiralling Rainbow)
      for (let i = 0; i < numBars; i++) {
        const rad = (i / numBars) * Math.PI * 2 + angle;
        const val = data[i % data.length] || 10;
        const barHeight = (val / 255) * 45 + 4;

        const x1 = centerX + Math.cos(rad) * baseRadius;
        const y1 = centerY + Math.sin(rad) * baseRadius;
        const x2 = centerX + Math.cos(rad) * (baseRadius + barHeight);
        const y2 = centerY + Math.sin(rad) * (baseRadius + barHeight);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);

        // Rainbow gradient alternating with Masser (crimson) & Secunda (silver/azure)
        const hue = (i * 10 + angle * 50) % 360;
        ctx.strokeStyle = status === 'speaking' 
          ? `hsla(${hue}, 90%, 60%, 0.85)` 
          : status === 'listening'
          ? `hsla(${200 + (i * 3)}, 85%, 65%, 0.8)`
          : 'rgba(148, 163, 184, 0.4)';

        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isOpen, status]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  if (!isOpen) return null;

  const handleToggleMute = () => {
    triggerHaptic(20);
    const muted = geminiLiveService.toggleMute();
    setIsMuted(muted);
  };

  const handleQuickCommand = (cmd: string) => {
    triggerHaptic(25);
    geminiLiveService.processSpokenQuery(cmd);
  };

  const handleManualSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualInput.trim()) return;
    triggerHaptic(20);
    geminiLiveService.processSpokenQuery(manualInput);
    setManualInput('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5 bg-black/85 backdrop-blur-xl animate-in fade-in duration-200">
      <div className="w-full max-w-lg h-[86vh] rounded-3xl bg-slate-950 border border-fissal-gold/50 shadow-2xl flex flex-col overflow-hidden relative">
        {/* Top Header */}
        <div className="p-4 bg-gradient-to-r from-amber-950/60 via-slate-900 to-black border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-amber-500/20 border border-fissal-gold/50 flex items-center justify-center text-fissal-gold shadow-quantum-glow">
              <Radio className="w-5 h-5 animate-pulse text-amber-400" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h2 className="font-cinzel text-xs font-bold text-white tracking-wider">
                  GEMINI 3.8 LIVE VOICE STUDIO
                </h2>
                <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">
                  DUPLEX
                </span>
              </div>
              <p className="text-[10px] text-slate-400 font-mono">
                Hands-Free Operator Co-Pilot • Real-Time VAD
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              triggerHaptic(15);
              onClose();
            }}
            className="p-2 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Acoustic Resonance Orb & Visualizer */}
        <div className="flex flex-col items-center justify-center pt-4 pb-2 relative shrink-0">
          <canvas
            ref={canvasRef}
            width={240}
            height={240}
            className="w-56 h-56"
          />

          {/* Center Microphone Orb Button */}
          <div className="absolute top-[88px] flex flex-col items-center">
            <button
              onClick={handleToggleMute}
              className={`w-20 h-20 rounded-full border flex items-center justify-center shadow-quantum-glow transition-all active:scale-95 ${
                isMuted
                  ? 'bg-rose-950/80 border-rose-500/60 text-rose-400'
                  : status === 'speaking'
                  ? 'bg-amber-500/20 border-fissal-gold text-fissal-gold shadow-quantum-glow'
                  : 'bg-sky-500/20 border-sky-400 text-sky-300'
              }`}
              title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
            >
              {isMuted ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
            </button>
          </div>

          {/* Status Badge */}
          <div className="mt-1 flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${
              status === 'speaking'
                ? 'bg-amber-400 animate-ping'
                : status === 'listening'
                ? 'bg-emerald-400 animate-pulse'
                : 'bg-sky-400'
            }`} />
            <span className="text-[11px] font-mono uppercase tracking-wider text-slate-300 font-semibold">
              {status === 'speaking' ? 'Fissal is Speaking' : isMuted ? 'Mic Muted' : 'Listening... (Speak Freely)'}
            </span>
          </div>
        </div>

        {/* Quick Diagnostic Spoken Commands */}
        <div className="px-4 py-2 flex items-center gap-1.5 overflow-x-auto scrollbar-none shrink-0 border-y border-white/5 bg-black/30">
          {[
            { label: 'Server Vitals', cmd: 'Read server vitals and Docker containers' },
            { label: 'Perimeter Cameras', cmd: 'Check perimeter cameras and alerts' },
            { label: 'Wi-Fi Latency', cmd: "What's my Wi-Fi latency to The Relay?" },
            { label: 'USB Serial Ports', cmd: 'Check USB serial baud rate' }
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => handleQuickCommand(item.cmd)}
              className="px-2.5 py-1 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-fissal-gold/40 text-slate-300 text-[10px] font-mono shrink-0 transition-all flex items-center gap-1"
            >
              <Sparkles className="w-2.5 h-2.5 text-fissal-gold" />
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Live Conversation Transcript */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 font-mono text-xs">
          {transcript.map((item) => (
            <div
              key={item.id}
              className={`p-3 rounded-2xl max-w-[88%] ${
                item.sender === 'echo'
                  ? 'ml-auto bg-amber-500/15 border border-fissal-gold/40 text-amber-200'
                  : 'mr-auto bg-slate-900/80 border border-white/10 text-slate-200 shadow-md'
              }`}
            >
              <div className="text-[10px] text-slate-400 mb-1 flex items-center gap-1">
                <span>{item.sender === 'echo' ? 'Echo 🗝️' : 'Fissal Prime 🐾'}</span>
                <span>•</span>
                <span>{new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              </div>
              <p className="leading-relaxed whitespace-pre-wrap">{item.text}</p>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>

        {/* Fallback Text Input Bar */}
        <form onSubmit={handleManualSend} className="p-3 bg-black/60 border-t border-white/10 flex items-center gap-2">
          <input
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            placeholder="Speak or type hands-busy instruction..."
            className="flex-1 bg-slate-900/90 border border-white/10 focus:border-fissal-gold/60 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 outline-none transition-all"
          />
          <button
            type="submit"
            className="p-2 rounded-xl bg-fissal-gold hover:bg-amber-400 text-slate-950 font-bold active:scale-95 transition-all"
            title="Send query"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};
